/**
 * Sentry initialization.
 *
 * Call `initSentry()` from `src/index.tsx` BEFORE `ReactDOM.render`.
 * When `REACT_APP_SENTRY_DSN` is empty, this is a full no-op — no network,
 * no instrumentation, no breadcrumbs.
 */
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";

const dsn = process.env.REACT_APP_SENTRY_DSN;

const environment = (() => {
  if (process.env.REACT_APP_SENTRY_ENVIRONMENT) {
    return process.env.REACT_APP_SENTRY_ENVIRONMENT;
  }
  const baseUrl = (process.env.REACT_APP_BASE_URL || "").toLowerCase();
  if (baseUrl.includes("live.travelme-paris.com") && !baseUrl.includes("pre-live")) {
    return "production";
  }
  if (baseUrl.includes("pre-live.travelme-paris.com")) {
    return "staging";
  }
  return "development";
})();

// eslint-disable-next-line @typescript-eslint/no-var-requires
const pkg = require("../package.json");
// Append the CI build id (short commit SHA, already embedded for version-check)
// so every deploy gets its own Sentry release — pkg.version alone sat at 1.1.0
// across months of deploys, forcing before/after triage by timestamp.
const buildId = process.env.REACT_APP_BUILD_ID || "dev";
const release = `tm-frontend@${pkg.version}+${buildId}`;

export function initSentry() {
  if (!dsn) {
    // eslint-disable-next-line no-console
    console.warn("[Sentry] REACT_APP_SENTRY_DSN not set — Sentry is disabled");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [new BrowserTracing()],

    // Performance sampling: 100% in dev/staging, 10% in prod
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // Don't send events during Jest test runs
    enabled: process.env.NODE_ENV !== "test",

    beforeSend(event) {
      // Strip sensitive query params from URLs
      if (event.request && event.request.url) {
        event.request.url = event.request.url.replace(
          /([?&])(token|api_key|secret|password)=[^&]*/gi,
          "$1$2=[REDACTED]"
        );
      }

      // Strip Authorization / Cookie headers
      if (event.request && event.request.headers) {
        const h = event.request.headers as Record<string, string>;
        delete h.Authorization;
        delete h.authorization;
        delete h.Cookie;
        delete h.cookie;
      }

      // Strip sensitive fields from request body
      if (event.request && event.request.data && typeof event.request.data === "object") {
        const data = event.request.data as Record<string, unknown>;
        ["password", "token", "api_key", "secret", "authorization"].forEach((key) => {
          if (key in data) data[key] = "[REDACTED]";
        });
      }

      // Scrub sensitive fields from breadcrumb data
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((crumb) => {
          if (crumb.data) {
            ["password", "token", "authorization"].forEach((key) => {
              if ((crumb.data as Record<string, unknown>)[key]) {
                (crumb.data as Record<string, unknown>)[key] = "[REDACTED]";
              }
            });
          }
          return crumb;
        });
      }

      return event;
    },
  });

  // eslint-disable-next-line no-console
  console.log(`[Sentry] Initialized — environment=${environment} release=${release}`);
}

export { Sentry };
